/**
 * The device-performance rules. A device lands in exactly one of three
 * categories (see ./category.js):
 *
 *   SMOOTH        — average processing FPS above smoothAboveFps
 *   LIMITED       — average processing FPS above limitedAboveFps
 *   NOT_SUPPORTED — anything at or below limitedAboveFps, or a device that
 *                   misses the RAM / OS requirements, or one whose camera or
 *                   pose model cannot start at all.
 *
 * The OS rule applies only to the platforms it names (Windows, macOS, Android,
 * iOS, iPadOS); other platforms are judged on FPS alone. RAM is judged only
 * where the browser reports it (navigator.deviceMemory, Chromium only). The
 * model / processor rules cannot be read by a browser — they are shown as notes.
 */
export const DEVICE_CATEGORY_RULES = Object.freeze({
  smoothAboveFps: 20,
  limitedAboveFps: 12,
  minRamGb: 8,
  minAndroidVersion: 12,
  minWindowsVersion: 10,
  minIosVersion: 15,
  minIpadOsVersion: 15,
  minMacOsVersion: 12,
  // Shown only — the browser never reveals the device model or processor.
  minIphoneModel: 12,
  requiredIpadChips: 'an M1, M2, M3 or M4 chip',
  requiredMacChips: 'an Apple M1, M2 or M3 processor or better',
  requiredWindowsCpu: 'an Intel 10th-gen or AMD Ryzen CPU or better',
});

/**
 * One benchmark = runCount identical runs with a cooldown between them; the
 * category comes from the average processing FPS of the runs.
 */
export const DEVICE_BENCHMARK = Object.freeze({
  // Not measured: model compile, first inferences, camera auto-exposure settling.
  warmupMs: 5000,
  // Warm-up also waits for the first real inference, up to this long.
  maxWarmupMs: 30000,
  measurementMs: 15000,
  runCount: 5,
  cooldownMs: 2000,
  // No camera/clip frame within this long = the source failed to start.
  firstFrameTimeoutMs: 10000,
});
